<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready" height="350" :options="options" :series="series" />
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-chart-interval="true"
                :with-daily-chart-interval="true"
                :with-yearly-chart-interval="true"
                :with-chart-period="true"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import DeparturesCardGrid from '../common/departures-card-grid.vue';
import { DASHBOARD_STAYLIO_ADOPTION_RATE_ENDPOINT } from '../../../../endpoints';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import { getWidgetGridRowsNumber, getApexChartsGradientFill, getDefaultChartPeriodBasedOnInterval } from '../helpers';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import { format, parseISO } from 'date-fns';
import { DASHBOARD_CHART_INTERVAL_MONTHLY } from '../../../../constants';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    mixins: [cardGridItemBaseMixin],
    name: 'StaylioAdoptionRateChartCardGridItem',
    components: {
        BusyModal,
        CardGridItem,
        TabWidgetSettingsModalForm,
        VueApexCharts,
        ResponsiveButton,
        GoBackIconButton,
        DeparturesCardGrid,
    },
    data() {
        return {
            autoWidthExclude: ['guest', 'property_name'],
            busy: null,
            data: [],
            selectedDay: null,
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
            dates: [],
        };
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_period:
                        this.tabWidget.settings.chart_period ||
                        getDefaultChartPeriodBasedOnInterval(DASHBOARD_CHART_INTERVAL_MONTHLY),
                    chart_interval: this.tabWidget.settings.chart_interval || DASHBOARD_CHART_INTERVAL_MONTHLY,
                },
            };
        },
        series() {
            return this.data.map((item) => {
                return {
                    name: item.name,
                    data: item.percentage,
                };
            });
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                },
                xaxis: {
                    type: 'datetime',
                    categories: this.dates,
                    labels: {
                        datetimeFormatter: {
                            month: 'MMM',
                            day: 'MMM dd',
                        },
                    },
                },
                yaxis: {
                    max: 100,
                    min: 0,
                    labels: {
                        formatter: (val) => `${val}%`,
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    custom: (params) => {
                        const { series, seriesIndex, dataPointIndex, w } = params;
                        const interval = this.tabWidget.settings.chart_interval || DASHBOARD_CHART_INTERVAL_MONTHLY;
                        let dateFormat = 'dd MMM, yyyy';

                        if (interval == 'YEARLY') {
                            dateFormat = 'yyyy';
                        } else if (interval == 'MONTHLY') {
                            dateFormat = 'MMM yyyy';
                        }

                        const date = format(new Date(this.dates[dataPointIndex]), dateFormat);

                        const filterData = this.data.map((item) => {
                            return {
                                bm: item.name,
                                total: item.total[dataPointIndex],
                                installed_staylio: item.installed_staylio[dataPointIndex],
                                percentage: item.percentage[dataPointIndex],
                            };
                        });

                        return `
                                <div class="apexcharts-tooltip-title" style="font-size:12px;">${date}</div>
                                <div style="padding:5px; font-size:12px; color: #111;">
                                    ${filterData
                                        .map((item, index) => {
                                            return `
                                            <div style="padding:5px;">
                                                <span style="background:${
                                                    w.globals.colors[index] || '#000'
                                                }; width:10px; height:10px; border-radius:50%; display: inline-block; margin-right: 5px;"></span>
                                                <span>${item.bm}: <b style="padding-left:5px;">${item.percentage}% (${
                                                item.installed_staylio
                                            } / ${item.total} Reservations)</b></span>
                                            </div>
                                        `;
                                        })
                                        .join('')}
                                            <div style="padding-top:5px; max-width:300px; text-wrap: wrap; font-size:11px; line-height: 1.5;">
                                                Adoption rate indicates the percentage of reservations that installed the Staylio app by check-in.
                                            </div>
                                        </div>
                            `;
                    },
                },
            };
        },
    },
    mounted() {
        this.fetchdata();
    },
    methods: {
        async fetchdata() {
            try {
                this.busy = true;
                const interval = this.tabWidget.settings.chart_interval || DASHBOARD_CHART_INTERVAL_MONTHLY;
                const period = this.tabWidget.settings.chart_period || getDefaultChartPeriodBasedOnInterval(interval);

                const response = await API.get(DASHBOARD_STAYLIO_ADOPTION_RATE_ENDPOINT(), {
                    params: {
                        period: period,
                        interval: interval,
                    },
                });

                const formatedDates = response.data.map((item) => {
                    if (interval === 'DAILY') {
                        return format(parseISO(item.date), 'MM/dd/yyyy');
                    } else if (interval === 'YEARLY') {
                        return format(parseISO(item.date), 'yyyy');
                    } else {
                        return format(parseISO(item.date), 'MMMM yyyy');
                    }
                });

                this.dates = formatedDates;

                const sources =
                    response.data.map((items) => {
                        const source = items.data.map((item) => {
                            return item.booking_method_case;
                        });
                        return source;
                    })[0] || [];

                const list = [];
                for (let i = 0; i < sources.length; i++) {
                    const source = sources[i];
                    const percentage = response.data.map((items) => {
                        let p = 0;
                        if (items.data[i]?.total > 0) {
                            p = (((items.data[i]?.installed_staylio || 0) / items.data[i]?.total) * 100).toFixed(0);
                        }
                        return p;
                    });

                    list.push({
                        name: source,
                        percentage: percentage,
                        total: response.data.map((items) => items.data[i]?.total || 0),
                        installed_staylio: response.data.map((items) => items.data[i]?.installed_staylio || 0),
                    });
                }

                this.data = list;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch chart data' });
            } finally {
                this.busy = false;
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
            this.cardGridKey += 1;
        },
    },
};
</script>

<style scoped></style>
